import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import { find, isRetailer } from "../../../../../common-deprecated/utils";
import { getValidatedConfig, ValidateConfigAction } from "../../../../shared-logic/utils/ValidateConfig";
import { type ExtraType, type PackType, RequestStatusEnum } from "../../../../../common-deprecated/types/CommonTypes";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";
import {
    type CarConfigBasketActionsTypes,
    addExtra,
    addPack,
    addPackExtra,
    addServiceProduct,
    calculatePrice,
    removeExtra,
    removePack,
    removePackExtra,
    removeAllPacksAndExtras,
    removeServiceProduct,
    resolvePackConflict,
    setColour,
    setConfiguration,
    setExtraOption,
    setUpholstery,
    setWheels,
    setWinterWheel,
} from "../slices/CarConfigBasketSlice";
import type { CarConfigLogicType } from "../store";
import CarDataConsolidator from "../../../../shared-logic/utils/classes/CarDataConsolidator";
import {
    modifyQueryParams,
    resolveAddExtraIncludExcludeRules,
    resolveRemoveExtraIncludeExcludeRules,
    resolveSetItemColourDependencies,
} from "../../utils/basketUtils";
import { calculateTotalPrice } from "../../../../shared-logic/utils/priceUtils";
import { getDefaultPriceObject } from "../../../../../common-deprecated/constants";
import type { BasketType } from "../../../../shared-logic/types/BasketType";
import {
    formatAEMCashPriceIntl,
    getAEMLicenseFee,
    getAemPriceConfig,
    showAemInsuranceRates,
} from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { resetFinanceRates, resetQuotes, startFinanceRateFetch } from "../slices/CarConfigFinanceSlice";
import { startExternalPriceValueFetch } from "../slices/carConfigExternalPriceSlice";
import { startInsuranceRateFetch } from "../slices/CarConfigInsuranceSlice";
import { resetSavedState, startConfigSave } from "../slices/CarConfigSaveSlice";
import {
    CarConfigStep,
    getCarConfigStepPath,
} from "../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { findPackExtraParent } from "../../../../shared-logic/utils/typedUtils";
import { loadTechSpecs } from "../../../../shared-logic/utils/techSpecUtils";
import { setSpecs } from "../slices/CarConfigSpecSlice";
import { setMotorization } from "../slices/CarConfigCarSlice";
import { hasAlternativeEcoSpecFlow } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { startEcoConsumptionCostsFetch } from "../../../../shared-modules/eco-consumption-section/redux/reducers/EcoConsumptionReducer";
import { getConfigSpecsPayloadSelector } from "../../utils/externalPriceUtils";
import { CarConfigConfigureSection } from "../../utils/types/SectionTypes";
import { showCarConfigMonthlyRate } from "../../utils/financeInsuranceRate";
import { getCarConfigLabel, getCarConfigOptionalLabel } from "../../utils/labelUtils";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

const calculatePriceLogic = (createLogic as CarConfigLogicType<ReturnType<typeof calculatePrice>>)({
    // Calculate price is always called even for NMSC's which don't have prices (EU/EN, ZZ/EN)
    type: calculatePrice.type,
    name: "calculatePriceLogic",

    // Disabled consistent return because returning void will break the code (freezes at getGrades)
    /* eslint-disable consistent-return */
    transform({ getState, action }, next, reject) {
        const { carConfigSettings, commonSettings, car, basket, externalPrice } = getState();
        // If externalPrice is enabled but still fetching the functions, do not calculate.
        // When done externalPrice logic will re-trigger calculate.
        if (carConfigSettings.enableExternalPrice && externalPrice.state === RequestStatusEnum.Error)
            return (reject as () => void)();

        // Check to see if monthly rates are enabled in the flexibilityMatrix
        const showMonthlyRates = showCarConfigMonthlyRate(commonSettings, car);

        // Calculate the total price
        const calculatedTotal = calculateTotalPrice(
            getDefaultPriceObject(),
            basket,
            car,
            showMonthlyRates,
        ) as BasketType["total"];

        // If externalPrice.error is true this means the externalPrice price has been calculated for the current configuration but failed.
        // In that case don't use externalPrice pricing even though externalPrice is enabled.
        if (carConfigSettings.enableExternalPrice && externalPrice.state !== RequestStatusEnum.Error) {
            // Check should change to see if something in the configuration changed
            // Always reload the external price, even if the price differs. There are scenario's when adding for instance a pack without price, it pushes
            // the taxes into a new tier (due to more emissions), if so the co2 value will update and so will the taxes on all items.
            if (action.payload.loadExternalPrice) {
                // externalPrice originalTotalPrice contains the last price where externalPrice rates were fetched for.
                // Trigger a new externalPrice price fetch, the fetch will trigger a new calculate action when done.
                // Loading external price also encapsulates loading mass management data
                return reject(startExternalPriceValueFetch({ originalTotalPrice: calculatedTotal }));
            }

            const { totalValue } = externalPrice.calculatedPrice;
            calculatedTotal.cash = totalValue;
            calculatedTotal.discount = totalValue;
            calculatedTotal.onlineCashDiscount = totalValue;
            calculatedTotal.onlineMonthlyDiscount = totalValue;
            calculatedTotal.discountTax = totalValue - externalPrice.originalTotalPrice.discount;
            calculatedTotal.discountWithoutTax = externalPrice.originalTotalPrice.discount;
            calculatedTotal.isExternalPrice = true;
        }

        // Fill in the license fee if applicable
        calculatedTotal.licenseFee = getAEMLicenseFee(
            commonSettings,
            car.price.cash,
            car.model.internalCode,
            AemFmComponent.Carconfig,
        );

        action.payload.total = calculatedTotal;

        if (DOMUtils.canUseDOM) {
            const showSummaryInsuranceRates = showAemInsuranceRates(
                commonSettings,
                AemFmComponent.Carconfig,
                "new",
                "",
            );

            if (showMonthlyRates || showSummaryInsuranceRates) {
                // If externalPrice is active, this happens after externalPrice price is fetched.
                // Don't update the finance rate if externalPrices are active, but an error occurred!
                if (carConfigSettings.enableExternalPrice && externalPrice.hadError) {
                    reject(resetFinanceRates());
                }
            }
        }

        // Set the current basktet total as a reference value for the process before it gets overwritten.
        action.payload.previousTotal = basket.total;

        next(action);
    },
    process({ getState, action }, dispatch, done) {
        const { commonSettings, car, carConfigSettings, specs, basket, utils } = getState();
        // Check to see if monthly rates are enabled in the flexibilityMatrix
        const showMonthlyRates = showCarConfigMonthlyRate(commonSettings, car);
        const showSummaryInsuranceRates = showAemInsuranceRates(commonSettings, AemFmComponent.Carconfig, "new", "");

        if (showMonthlyRates) {
            dispatch(
                // Aligned with the startFinanceRateFetch in FinanceInsuranceLogic -> updateQuoteConfig

                startFinanceRateFetch({
                    trigger: action.payload.firstTime ? FinanceUpdateTrigger.NewQuote : FinanceUpdateTrigger.NewConfig,
                }),
            );
        }
        if (showSummaryInsuranceRates) dispatch(startInsuranceRateFetch());
        if (hasAlternativeEcoSpecFlow(commonSettings)) {
            dispatch(
                startEcoConsumptionCostsFetch({
                    ecoConsumptionCostService: carConfigSettings.ecoConsumptionCostService,
                    commonSettings,
                    config: getConfigSpecsPayloadSelector(getState(), true),
                    techSpecs: specs.techSpecs,
                    engine: car.motorization.engine,
                }),
            );
        }

        dispatch(startConfigSave());

        // If the current basket total price has changed since the previous version, dispatch a notification to let Screenreaders know.
        if (
            basket.total.cash !== action.payload.previousTotal?.cash ||
            basket.total.discount !== action.payload.previousTotal?.discount
        ) {
            const priceConfig = getAemPriceConfig(commonSettings, AemFmComponent.Carconfig, car.model.internalCode);
            const cashPrice = formatAEMCashPriceIntl(action.payload.total?.discount || "", commonSettings, priceConfig);

            dispatch(
                addNotification([
                    {
                        title: getCarConfigLabel({ commonSettings, carConfigSettings }, "carConfigA11yPriceUpdated"),
                        description: getCarConfigLabel(
                            { commonSettings, carConfigSettings },
                            "carConfigA11yPriceUpdatedDescription",
                            {
                                price:
                                    utils.financeOption === "cash"
                                        ? cashPrice.primaryPrice
                                        : action.payload.total?.monthly,
                            },
                        ),
                        srOnly: true,
                    },
                ]),
            );
        }

        done();
    },
});

const setColourLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setColour>>)({
    type: setColour.type,

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, carConfigSettings } = getState();
        const validatedConfig = getValidatedConfig(
            basket,
            ValidateConfigAction.Colour,
            items,
            action.payload.colour.id,
        );
        dispatch(setConfiguration(validatedConfig));

        const [packRemovedNotificationTitleLabel, packRemovedNotificationLabel, viewPacksLabel] = getCarConfigLabel(
            { commonSettings, carConfigSettings },
            [
                "carConfigPackRemovedNotificationTitle",
                "carConfigPackRemovedDueIncompatibleColourNotification",
                "carConfigViewPacks",
            ],
        );

        if (validatedConfig.packs.length < basket.packs.length) {
            dispatch(
                addNotification([
                    {
                        title: packRemovedNotificationTitleLabel,
                        description: packRemovedNotificationLabel,
                        action: {
                            closeButton: true,
                            targetButton: { text: viewPacksLabel, target: CarConfigConfigureSection.Packs },
                        },
                    },
                ]),
            );
        }
        resolveSetItemColourDependencies(getState, dispatch);
        done();
    },
});

const setUpholsteryLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setUpholstery>>)({
    type: setUpholstery.type,

    validate({ getState, action }, allow, reject) {
        const { upholstery } = action.payload;
        // This needs to be done for now as there is some weird things happening here which i am not sure why
        if ((upholstery as unknown as ExtraType).isUpholstery) {
            // Check if it is a pack upholstery
            const { basket } = getState();
            const basketPackUpholsteries = CarDataConsolidator.getBasketPackUpholsteries(
                basket.packs,
                true,
                basket.colour.id,
            );
            if (basketPackUpholsteries.length && find(upholstery.id, basketPackUpholsteries)) {
                // EquipmentUpholstery part of a pack
                const parentPack = findPackExtraParent(upholstery.id, basket.packs);
                reject(
                    addPackExtra({
                        pack: parentPack,
                        extra: upholstery as unknown as ExtraType,
                        selectedExtras: [],
                        automatic: false,
                    }),
                );
            } else {
                // EquipmentUpholstery, which is an extra so use addExtra instead.
                // ValidateConfig will take care of the applicable upholstery logic.
                reject(
                    addExtra({
                        extra: upholstery as unknown as ExtraType,
                        automatic: false,
                    }),
                );
            }
        } else {
            allow(action);
        }
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, carConfigSettings } = getState();
        const validatedConfig = getValidatedConfig(
            basket,
            ValidateConfigAction.Upholstery,
            items,
            action.payload.upholstery.id,
        );
        dispatch(setConfiguration(validatedConfig));

        const [packRemovedNotificationTitleLabel, packRemovedNotificationLabel, viewPacksLabel] = getCarConfigLabel(
            { commonSettings, carConfigSettings },
            [
                "carConfigPackRemovedNotificationTitle",
                "carConfigPackRemovedDueIncompatibleUpholsteryNotification",
                "carConfigViewPacks",
            ],
        );

        if (validatedConfig.packs.length < basket.packs.length) {
            dispatch(
                addNotification([
                    {
                        title: packRemovedNotificationTitleLabel,
                        description: packRemovedNotificationLabel,
                        action: {
                            closeButton: true,
                            targetButton: { text: viewPacksLabel, target: CarConfigConfigureSection.Packs },
                        },
                    },
                ]),
            );
        }

        resolveSetItemColourDependencies(getState, dispatch);
        done();
    },
});

const addExtraLogic = (createLogic as CarConfigLogicType<ReturnType<typeof addExtra>>)({
    type: addExtra.type,

    validate({ getState, action }, allow, reject) {
        const { items, basket } = getState();
        const { extras } = basket;

        // Equipment is already added, do nothing.
        if (find(action.payload.extra.id, extras)) {
            return (reject as () => void)();
        }

        const conflicts = CarDataConsolidator.extraHasConflicts(action.payload.extra, items.extras, extras);
        const parentPack = basket.packs.length ? findPackExtraParent(action.payload.extra.id, basket.packs) : null;
        const parentPackEquipment = parentPack ? find(action.payload.extra.id, parentPack.equipment) : false;
        if (parentPack && parentPackEquipment) {
            // Add an extra as packExtra when the pack is already selected
            allow(
                addPackExtra({
                    pack: parentPack,
                    extra: parentPackEquipment as ExtraType,
                    selectedExtras: [],
                    automatic: false,
                }),
            );
        } else if (!conflicts) {
            allow(action);
        } else {
            return (reject as () => void)();
            // TODO: modals
            // showModal({
            //     type: MODAL_EXTRA_CONFLICT,
            //     extra: action.extra,
            //     conflicts,
            //     extraPendingStack: action.extraPendingStack || [],
            // }),
        }
    },

    process({ getState, action }, dispatch, done) {
        resolveAddExtraIncludExcludeRules(getState, action.payload.extra, dispatch);
        if (
            action.payload.extra.isUpholstery ||
            action.payload.extra.includePacks.length ||
            action.payload.extra.excludePacks.length
        ) {
            const { basket, items } = getState();
            const validatedConfig = getValidatedConfig(
                basket,
                ValidateConfigAction.AddExtra,
                items,
                action.payload.extra.id,
            );
            dispatch(setConfiguration(validatedConfig));
        }
        resolveSetItemColourDependencies(getState, dispatch);
        done();
    },
});

const removeExtraLogic = (createLogic as CarConfigLogicType<ReturnType<typeof removeExtra>>)({
    type: removeExtra.type,

    validate({ getState, action }, allow, reject) {
        const { basket } = getState();

        // Equipment is already added, do nothing.
        if (!find(action.payload.extra.id, basket.extras)) {
            return (reject as () => void)();
        }

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        resolveRemoveExtraIncludeExcludeRules(getState, action.payload.extra, dispatch);
        if (action.payload.extra.includePacks.length || action.payload.extra.excludePacks.length) {
            const { basket, items } = getState();
            const validatedConfig = getValidatedConfig(
                basket,
                ValidateConfigAction.RemoveExtra,
                items,
                action.payload.extra.id,
            );
            dispatch(setConfiguration(validatedConfig));
        }
        done();
    },
});

const setExtraOptionLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setExtraOption>>)({
    type: setExtraOption.type,

    validate({ getState, action }, allow, reject) {
        const { basket } = getState();

        const extraSelected = !!find(action.payload.extra.id, basket.extras);
        if (!extraSelected) reject(addExtra({ extra: action.payload.extra, automatic: false }));
        else allow(action);
    },
});

const addPackSelectionLogic = (createLogic as CarConfigLogicType<ReturnType<typeof addPack | typeof addPackExtra>>)({
    type: [addPack.type, addPackExtra.type],

    validate({ getState, action }, allow, reject) {
        const { basket, items } = getState();
        if (
            (action.type === addPack.type && !!find(action.payload.pack.id, basket.packs)) ||
            (action.type === addPackExtra.type &&
                !!find((action as ReturnType<typeof addPackExtra>).payload.extra.id, basket.packExtras))
        ) {
            return (reject as () => void)();
        }

        // Extract action variables.
        const { selectedExtras, selectedPacks, automatic, pack, extra } = (action as ReturnType<typeof addPack>)
            .payload;

        // Add new equipment to selectedExtras.
        // The action is automatic when it is called by resolve functions and not directly by the user.
        // This means the addedPack is already added to the selectedExtras.
        if (extra && !automatic) {
            selectedExtras.push(extra);
        }

        // For conflict resolution we need to combine the currently selected extras and the basket extras.
        // Get all the selected packExtras excluding the selectedExtras
        const additionalExtras = basket.packExtras.filter((packExtra) => !find(packExtra.id, selectedExtras));
        let combinedSelectedExtras = automatic ? selectedExtras : [...selectedExtras, ...additionalExtras];
        // Remove the excluded equipment from the combined selected extras!
        if (extra) {
            // Array used to determine the standard conflicts, filter out the excludes present in the newly selected pack extra
            combinedSelectedExtras = combinedSelectedExtras.filter(
                (selectedExtra: ExtraType) => extra.excluded.indexOf(selectedExtra.id) === -1,
            );
        }

        // Check for conflicts within standard packs.
        let standardConflicts = CarDataConsolidator.packHasStandardConflicts(
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.visualItems.standard),
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.visualItems.optional),
            CarDataConsolidator.getExtrasBasedOnColour(basket, pack.equipment as ExtraType[]),
            combinedSelectedExtras,
        );
        if (standardConflicts) {
            // Sort the standardConflicts based on their index in the pack.equipment for visual consistency in the overlay
            standardConflicts = pack.equipment.filter(
                (packExtra) => !!find(packExtra.id, standardConflicts as ExtraType[]),
            ) as ExtraType[];
            return (reject as () => void)();
            // TODO: modal
            // showModal({
            //     type: MODAL_PACK_CONFLICT,
            //     pack,
            //     conflicts: standardConflicts,
            //     selectedConflicts: selectedExtras,
            //     isConflictBetweenPackExtras: true,
            // }),
        }

        // Check for conflicts within optional packs, which can be handled by extra logic.
        if (extra) {
            let equipmentConflicts = CarDataConsolidator.extraHasConflicts(
                extra,
                CarDataConsolidator.getExtrasBasedOnColour(basket, pack.equipment as ExtraType[]),
                combinedSelectedExtras,
            );
            if (equipmentConflicts) {
                // Sort the equipmentConflicts based on their index in the pack.equipment for visual consistency in the overlay
                equipmentConflicts = pack.equipment.filter(
                    (packExtra) => !!find(packExtra.id, equipmentConflicts as ExtraType[]),
                ) as ExtraType[];
                return (reject as () => void)();
                // TODO: modal
                // showModal({
                //     type: MODAL_PACK_CONFLICT,
                //     pack,
                //     conflicts: equipmentConflicts,
                //     selectedConflicts: selectedExtras,
                //     isConflictBetweenPackExtras: true,
                // }),
            }
        }

        // Check for conflicts between packs
        const packConflicts = CarDataConsolidator.packHasConflictsBetweenPacks(
            pack,
            items.packs,
            basket.packs.concat(selectedPacks || []),
        );
        if (packConflicts) {
            return (reject as () => void)();
            // TODO: modals
            // showModal({
            //     type: MODAL_PACK_CONFLICT,
            //     pack,
            //     conflicts: packConflicts,
            //     selectedConflicts: selectedPacks || [],
            //     isConflictBetweenPackExtras: false,
            // }),
        }

        try {
            allow({
                ...action,
                payload: { ...action.payload, combinedSelectedExtras, selectedExtras },
            } as ReturnType<typeof addPack | typeof addPackExtra>);
        } catch (exception) {
            return (reject as () => void)();
        }
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, carConfigSettings } = getState();

        const selectedItem =
            action.type === addPack.type
                ? action.payload.pack.id
                : (action as ReturnType<typeof addPackExtra>).payload.extra.id;
        const actionString =
            action.type === addPack.type ? ValidateConfigAction.AddPack : ValidateConfigAction.AddPackExtra;
        const validatedConfig = getValidatedConfig(basket, actionString, items, selectedItem);

        // True when an extra was removed from the basket and not added as packExtra
        const extraRemoved = basket.extras.some(
            (extra) => !find(extra.id, validatedConfig.extras) && !find(extra.id, validatedConfig.packExtras),
        );

        const [extrasRemovedNotificationTitle, extrasRemovedNotificationDescription] = getCarConfigLabel(
            { commonSettings, carConfigSettings },
            ["extrasRemovedNotificationTitle", "extrasRemovedNotificationDescription"],
        );

        if (extraRemoved) {
            dispatch(
                addNotification([
                    {
                        title: extrasRemovedNotificationTitle,
                        description: extrasRemovedNotificationDescription,
                        action: {
                            closeButton: true,
                        },
                    },
                ]),
            );
        }

        dispatch(setConfiguration(validatedConfig));

        done();
    },
});

const setConfigurationLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setConfiguration>>)({
    type: [setConfiguration.type],
    latest: true,

    process({ getState }, dispatch, done) {
        const state = getState();
        const isToyotaRetailer = isRetailer(getState().commonSettings) && getState().commonSettings.brand === "toyota";
        if (!isToyotaRetailer) {
            loadTechSpecs(state.commonSettings, state.car, state.basket, state.items, state.utils.financeOption)
                .then((specs) => {
                    dispatch(setSpecs(specs));
                })
                .finally(() => {
                    done();
                });
        }
    },
});

const setMotorizationLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setMotorization>>)({
    type: setMotorization.type,

    process(_, dispatch, done) {
        dispatch(resetQuotes());
        done();
    },
});

const removePackSelectionLogic = (createLogic as CarConfigLogicType<ReturnType<typeof removePack>>)({
    type: removePack.type,

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, carConfigSettings, car } = getState();
        const selectedItem = action.payload.pack.id;
        const actionString = ValidateConfigAction.RemovePack;
        const validatedConfig = getValidatedConfig(basket, actionString, items, selectedItem);

        const [optionalPackExtrasRemovedNotificationTitle, optionalPackExtrasRemovedNotificationDescription] =
            getCarConfigOptionalLabel({ commonSettings, carConfigSettings }, [
                "carConfigOptionalPackExtrasRemovedNotificationTitle",
                "carConfigOptionalPackExtrasRemovedNotificationDescription",
            ]);

        // Show a notification when a selected optional extra from a pack (packExtra) was removed because of deselecting a pack
        if (
            validatedConfig.packExtras.length !== basket.packExtras.length &&
            optionalPackExtrasRemovedNotificationTitle &&
            optionalPackExtrasRemovedNotificationDescription
        ) {
            dispatch(
                addNotification([
                    {
                        title: optionalPackExtrasRemovedNotificationTitle,
                        description: optionalPackExtrasRemovedNotificationDescription,
                        action: { closeButton: true },
                    },
                ]),
            );
        }

        // When packs are removed because of inclusion rules, they also need to be removed from the query.
        modifyQueryParams(
            commonSettings,
            dispatch,
            basket,
            car,
            getCarConfigStepPath(CarConfigStep.Configure, car.model.id, car.id),
            [{ param: "p", shortIds: validatedConfig.packs.map((pack) => pack.shortId), action: "set" }],
        );

        dispatch(setConfiguration(validatedConfig));
        done();
    },
});

const removeAllPacksAndExtrasLogic = (createLogic as CarConfigLogicType<ReturnType<typeof removeAllPacksAndExtras>>)({
    type: removeAllPacksAndExtras.type,
    process({ getState }, dispatch, done) {
        const { basket, carConfigNavigation, car } = getState();
        modifyQueryParams(
            carConfigNavigation.basketParams,
            dispatch,
            basket,
            car,
            getCarConfigStepPath(CarConfigStep.Configure, car.model.id, car.id),
            [
                { param: "p", shortIds: [], action: "set" },
                { param: "e", shortIds: [], action: "set" },
            ],
        );
        done();
    },
});

const removePackExtraSelectionLogic = (createLogic as CarConfigLogicType<ReturnType<typeof removePackExtra>>)({
    type: removePackExtra.type,

    validate({ action }, allow, reject) {
        // Do not allow deselection when the pack extra is standard
        // (in the case 2 standard pack extras exclude eachother they show as optional since you need to make a selection)
        if (action.payload.extra && action.payload.extra.standard) return (reject as () => void)();

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const { basket, items, commonSettings, carConfigSettings } = getState();
        const selectedItem = action.payload.extra.id;
        const actionString = ValidateConfigAction.RemovePackExtra;
        const validatedConfig = getValidatedConfig(basket, actionString, items, selectedItem);

        const [optionalPackExtrasRemovedNotificationTitle, optionalPackExtrasRemovedNotificationDescription] =
            getCarConfigOptionalLabel({ commonSettings, carConfigSettings }, [
                "carConfigOptionalPackExtrasRemovedNotificationTitle",
                "carConfigOptionalPackExtrasRemovedNotificationDescription",
            ]);

        // Show a notification when a selected optional extra from a pack (packExtra) was removed because of deselecting a pack
        if (
            validatedConfig.packExtras.length !== basket.packExtras.length &&
            optionalPackExtrasRemovedNotificationTitle &&
            optionalPackExtrasRemovedNotificationDescription
        ) {
            dispatch(
                addNotification([
                    {
                        title: optionalPackExtrasRemovedNotificationTitle,
                        description: optionalPackExtrasRemovedNotificationDescription,
                        action: { closeButton: true },
                    },
                ]),
            );
        }
        dispatch(setConfiguration(validatedConfig));
        done();
    },
});

const resolvePackConflictLogic = (createLogic as CarConfigLogicType<ReturnType<typeof resolvePackConflict>>)({
    type: resolvePackConflict.type,

    process({ action }, dispatch, done) {
        const { pack, selectedItem, selectedConflicts, conflicts, isConflictBetweenPackExtras } = action.payload;

        // Filter conflict ids from selectedConflicts.
        const resolvedItems = (selectedConflicts as PackType[]).filter(
            (item) => !find(item.id, conflicts as PackType[]),
        );
        // Add selected item (pack/extra) which resolved the conflict.
        resolvedItems.push(selectedItem as PackType);

        // Get removed extras and remove them from basket only relevant when resolving conflicts between pack extras
        if (isConflictBetweenPackExtras) {
            const removedExtras = (selectedConflicts as ExtraType[]).filter(
                (extra) => !!find(extra.id, conflicts as ExtraType[]),
            );
            removedExtras.forEach((removedExtra) => dispatch(removePackExtra({ extra: removedExtra })));
        } else {
            // In case of resolving a pack conflict we also need to add the current pack (which is being selected and caused the conflicts in the first place) to the resolvedItems array
            // Doing so will make sure that no double conflicts are triggered (if the "selectedItem" also spawns a conflict dialogue due to conflicting include rules towards "pack").
            resolvedItems.push(pack);
            dispatch(
                addPack({
                    pack: selectedItem as PackType,
                    selectedExtras: [],
                    selectedPacks: resolvedItems,
                    automatic: true,
                }),
            );
        }

        // There have been no scenarios which combine conflicting pack extras and conflicting pack includes so once we encounter such a scenario this might require fine tuning
        dispatch(
            addPack(
                {
                    pack,
                    selectedExtras: isConflictBetweenPackExtras ? (resolvedItems as unknown as ExtraType[]) : [],
                    selectedPacks: !isConflictBetweenPackExtras ? resolvedItems : [],
                    automatic: true,
                },
                // isConflictBetweenPackExtras ? (resolvedItems as unknown as ExtraType[]) : [],
                // !isConflictBetweenPackExtras ? resolvedItems : [],
                // true,
                // false,
                // false,
                // action.transaction,
            ),
        );

        done();
    },
});

// Service plan logic
const addServiceProductLogic = (createLogic as CarConfigLogicType<ReturnType<typeof addServiceProduct>>)({
    type: addServiceProduct.type,
    validate({ getState, action }, allow, reject) {
        const { basket, items } = getState();

        if (find(action.payload.serviceProduct.id, basket.serviceProducts)) {
            return (reject as () => void)();
        }

        const serviceProductConflicts = CarDataConsolidator.getServiceProductConflicts(
            action.payload.serviceProduct,
            items.serviceProducts,
            basket.serviceProducts,
        );

        if (serviceProductConflicts.length > 0) {
            // TODO: MODALS?
            //     // If there are conflicts. Open conflict modal
            //     return reject(
            //         showModal({
            //             type: MODAL_SERVICE_PRODUCT_CONFLICT,
            //             serviceProduct: action.serviceProduct,
            //             conflicts: serviceProductConflicts,
            //         }),
            //     );
        }

        allow(action);
    },
    process({ getState, action }, dispatch, done) {
        const { items } = getState();

        const excludedServiceProducts = CarDataConsolidator.getExcludedServiceProducts(
            action.payload.serviceProduct,
            items.serviceProducts,
        );

        const includedServiceProducts = CarDataConsolidator.getIncludedServiceProducts(
            action.payload.serviceProduct,
            items.serviceProducts,
        );

        if (includedServiceProducts.length > 0) {
            includedServiceProducts.forEach((includedServiceProduct) => {
                dispatch(addServiceProduct({ serviceProduct: includedServiceProduct }));
            });
        }

        if (excludedServiceProducts.length > 0) {
            excludedServiceProducts.forEach((excludedServiceProduct) => {
                dispatch(removeServiceProduct({ serviceProduct: excludedServiceProduct }));
            });
        }

        done();
    },
});

const removeServiceProductLogic = (createLogic as CarConfigLogicType<ReturnType<typeof removeServiceProduct>>)({
    type: removeServiceProduct.type,
    process({ getState, action }, dispatch, done) {
        const { items } = getState();
        const { serviceProduct } = action.payload;

        const includedServiceProducts = CarDataConsolidator.getIncludedServiceProducts(
            serviceProduct,
            items.serviceProducts,
        );

        // Remove the included service Products as they are a part of this serviceProduct
        if (includedServiceProducts.length > 0) {
            includedServiceProducts.forEach((includedServiceProduct) => {
                dispatch(removeServiceProduct({ serviceProduct: includedServiceProduct }));
            });
        }

        done();
    },
});

const setWheelLogic = (createLogic as CarConfigLogicType<ReturnType<typeof setWheels>>)({
    type: setWheels.type,
    validate({ getState, action }, allow, reject) {
        const { basket, items } = getState();
        const { extras } = basket;

        if (basket.wheel.id === action.payload.wheel.id) return (reject as () => void)();

        const conflicts = CarDataConsolidator.extraHasConflicts(
            action.payload.wheel as unknown as ExtraType,
            items.extras,
            extras,
        );

        if (conflicts) return (reject as () => void)();
        // TODO: MODALS?
        // showModal({
        //     type: MODAL_EXTRA_CONFLICT,
        //     extra: action.wheel as unknown as ExtraType,
        //     conflicts,
        //     extraPendingStack: action.extraPendingStack || [],
        // }),

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        resolveAddExtraIncludExcludeRules(getState, action.payload.wheel as unknown as ExtraType, dispatch);
        done();
    },
});
const updateBasketLogic = (createLogic as CarConfigLogicType<CarConfigBasketActionsTypes>)({
    type: [
        setColour.type,
        setUpholstery.type,
        addPack.type,
        addPackExtra.type,
        removePack.type,
        removePackExtra.type,
        addExtra.type,
        removeExtra.type,
        addServiceProduct.type,
        removeServiceProduct.type,
        setWheels.type,
        setWinterWheel.type,
        setConfiguration.type,
    ],
    name: "updateBasketLogic",
    process({ getState }, dispatch, done) {
        const { basket, car, carConfigNavigation } = getState();

        const path = getCarConfigStepPath(CarConfigStep.Configure, car.model.id, car.id);

        modifyQueryParams(carConfigNavigation.basketParams, dispatch, basket, car, path);

        // CCSave doesn't necessarily update when updating the basket throughout the application.
        // It's important to reset the saved state boolean so the application can know when it should get fetched again.
        dispatch(resetSavedState());
        dispatch(calculatePrice({ loadExternalPrice: true }));
        done();
    },
});

export default [
    calculatePriceLogic,
    updateBasketLogic,
    setColourLogic,
    setUpholsteryLogic,
    addPackSelectionLogic,
    removePackSelectionLogic,
    removeAllPacksAndExtrasLogic,
    removePackExtraSelectionLogic,
    resolvePackConflictLogic,
    addExtraLogic,
    removeExtraLogic,
    addServiceProductLogic,
    removeServiceProductLogic,
    setExtraOptionLogic,
    setWheelLogic,
    setConfigurationLogic,
    setMotorizationLogic,
    // ...HistoryLogic,
];

export const basketServerLogic = [calculatePriceLogic, updateBasketLogic];
